import { useSignal } from '@preact/signals'
import { AuthErrorCodes } from 'firebase/auth'
import { useLocation } from 'wouter-preact'

import { Button, Input, Typography } from 'components/UI'
import { signInWithLinkConfirmation } from 'state/auth/sign_in_passwordless_confirmation'
import { emitSystemNotification } from 'state'
import codigoLogo from 'assets/icons/codigo-logo.svg'

import {
	emailState,
	emailFieldErrorState,
	setEmail,
	setEmailFieldError
} from './state'

type Props = {
	onEmailValidationInvalidLink: () => void
}

const VerifyEmail = ({ onEmailValidationInvalidLink }: Props) => {
	const isEmailBeingVerified = useSignal(false)
	const [, setLocation] = useLocation()

	const handleSignIn = async (e: Event) => {
		e.preventDefault()

		isEmailBeingVerified.value = true

		const response = await signInWithLinkConfirmation(emailState.value)

		isEmailBeingVerified.value = false

		if (response?.error) {
			if (response.error.code === AuthErrorCodes.INVALID_EMAIL) {
				setEmailFieldError(response.error.message)
				return
			}

			if (response.error.code === AuthErrorCodes.INVALID_OOB_CODE) {
				setLocation('/', { replace: true })

				emitSystemNotification({
					display: 'dialog',
					variant: 'warning',
					title: 'Link Expired.',
					message:
						'The link you are trying to access has expired or is invalid. Please log in again.',
					acceptText: 'Go to Log In'
				})

				onEmailValidationInvalidLink()
			}
		}
	}

	return (
		<div class='grid h-screen w-screen place-content-center'>
			<div class='absolute top-5 flex w-full justify-center'>
				<img alt='Código logo' class='h-10' src={codigoLogo} />
			</div>

			<div class='flex w-[30rem] flex-col items-center rounded-lg border border-black-600 p-8 pb-12'>
				<Typography
					class='text-center'
					color='text-product'
					text='Verify Email'
					variant='h2'
				/>
				<div class='mb-8 mt-6 text-center'>
					<Typography
						color='text-white'
						text='Before you get started please confirm your email,'
						variant='text2'
					/>
					<Typography
						color='text-white'
						text='use the same email address you enter in the sign up.'
						variant='text2'
					/>
				</div>

				<form class='w-full space-y-7' onSubmit={handleSignIn}>
					<Input
						error={emailFieldErrorState.value}
						hasError={!!emailFieldErrorState.value}
						id='email-field'
						label='Email'
						name='email'
						onInput={setEmail}
						placeholder='john.doe@example'
						type='email'
						value={emailState}
						fullWidth
					/>
					<div>
						<Button
							className='mt-10'
							disabled={
								isEmailBeingVerified.value ||
								Boolean(emailState.value === '' || emailFieldErrorState.value)
							}
							isLoading={isEmailBeingVerified.value}
							text='Verify Email'
							type='submit'
							fullWidth
						/>
					</div>
				</form>
			</div>
		</div>
	)
}

export { VerifyEmail }
